import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import apiCall from "shared/services/ApiAdapter";
import masterApiCall from "shared/services/MasterDataApi";
import useAssignments from "shared/hooks/useAssignments";
import { QuestionMetricsPanel } from "modules/questions/QuestionMetricsPanel";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";

// Simple SVG for a loading spinner
const LoadingSpinner = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-8 w-8 text-indigo-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const getLabelByCode = (masterData, category, code) => {
  console.log(masterData);
  if (!masterData || !masterData[category]) {
    return "";
  }
  const item = masterData[category].find((item) => item.value === code);
  return item ? item.label : "";
};

export const LoadingIndicator = () => (
  <div className="flex justify-center items-center h-40 text-gray-700 dark:text-gray-300">
    <LoadingSpinner />
    <span className="ml-4 text-lg">Loading...</span>
  </div>
);

export const ErrorMessage = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center text-red-700 bg-red-50 dark:bg-red-900 dark:text-red-200 rounded-xl shadow-lg">
    <span className="text-4xl mb-4">⚠️</span>
    <p className="text-lg font-semibold mb-4">{message}</p>
    <button
      onClick={onRetry}
      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300"
    >
      Retry
    </button>
  </div>
);

// Helper component for a generic card
const Card = ({ children, className = "", borderColor = "" }) => (
  <div
    className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-l-4 ${borderColor} ${className}`}
  >
    {children}
  </div>
);


// Sidebar Component
export const Sidebar = ({
  activeSection,
  setActiveSection,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const navItems = [
    { name: "Dashboard", icon: "🏠", section: "dashboard" },
    { name: "My Courses", icon: "📚", section: "my-courses" },
    { name: "My Batches", icon: "🧑‍🤝‍🧑", section: "my-batches" },
    { name: "My Concepts", icon: "🎯", section: "my-concepts", beta: true },
    { name: "My Tutors", icon: "👨‍🏫", section: "my-tutors" },
    { name: "Assignments", icon: "📝", section: "my-assignments" },
    { name: "Results", icon: "📊", section: "my-results" },
  ];

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 md:hidden ${isSidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setIsSidebarOpen(false)}
      ></div>

      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-gray-800 dark:bg-gray-950 text-white flex flex-col p-6 shadow-2xl z-[60] transform md:relative md:translate-x-0 md:z-auto transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          }`}
      >
        <div className="mb-10 text-center">
          <span className="text-4xl">🎓</span>
          <h3 className="text-xl font-extrabold mt-2">Student Hub</h3>
        </div>
        <nav className="flex-1 space-y-3">
          {navItems.map((item) => (
            <button
              key={item.section}
              onClick={() => {
                setActiveSection(item.section);
                setIsSidebarOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-lg font-medium transition duration-200 text-left ${activeSection === item.section
                ? "bg-gray-700 text-indigo-300"
                : "text-gray-300 hover:bg-gray-700"
                }`}
            >
              <span className="mr-3">{item.icon}</span>
              <div className="relative inline-block flex-1">
                <span>{item.name}</span>
                {item.beta && (
                  <span
                    className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white bg-indigo-500 rounded-full shadow-md transform rotate-12 z-10"
                  >
                    beta
                  </span>
                )}
              </div>
            </button>
          ))}
        </nav>
        {/* <div className="mt-8 pt-6 border-t border-gray-700 text-center">
                    <button className="w-full py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition duration-300">
                        <span className="mr-2">🚪</span> Logout
                    </button>
                </div> */}
      </aside>
    </>
  );
};

// Header Component
export const Header = ({
  student,
  useMockData,
  setUseMockData,
  joinQuiz,
}) => (
  <header className="top-header fixed top-0 left-0 right-0 md:relative flex items-center p-4 sm:p-6 md:p-0 bg-white dark:bg-gray-900 md:bg-transparent md:dark:bg-transparent shadow-md md:shadow-none z-30">
    <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
      {/* Student name */}
      <div className="flex items-center space-x-2 min-w-0 flex-1">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-gray-100 truncate">
          {student?.name}
        </h3>
      </div>

      {/* Button */}
      <ProtectedVisibility requiredRoles={["ROLE_STUDENT", "ROLE_PARENT"]}>
        <div className="flex items-center space-x-2 flex-shrink-0">
    <button
            onClick={() => { joinQuiz() }}
            className="bg-gray-700 text-gray-100 px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2 rounded-lg shadow hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-sm sm:text-base md:text-lg font-semibold"
          >
            <div className="relative inline-block">
              <span>Join Quiz</span>
              <span
                className="absolute -top-2 -right-4 sm:-right-6 
               px-1.5 py-0.5 sm:px-2 text-[9px] sm:text-[10px] font-bold tracking-wide
               text-gray-800 bg-gray-100 rounded-full 
               dark:bg-gray-700 dark:text-gray-300
               transform rotate-12 shadow-sm"
              >
                beta
                    </span>
      </div>
          </button>
        </div>
      </ProtectedVisibility>
    </div>
  </header>
);

// Progress Bar Component
const ProgressBar = ({ progress }) => (
  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden w-full md:w-64">
    <div
      className="h-full bg-indigo-600 rounded-full transition-all duration-500"
      style={{ width: `${progress}%` }}
    ></div>
  </div>
);

// Dashboard "Snapshot" Card Component
const SnapshotCard = ({ number, text, colorClass }) => (
  <div
    className={`flex flex-col items-center justify-center p-4 rounded-lg ${colorClass}`}
  >
    <p className="text-4xl sm:text-5xl font-extrabold text-gray-800 dark:text-white">
      {number}
    </p>
    <p className="font-semibold mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
      {text}
    </p>
  </div>
);

// Reusable Course Card Component
const CourseCard = ({ course, masterData }) => {
  const { t } = useTranslation();

  const getProgress = (courseId) => {
    // This would be a real API call to get progress. Simulating it here.
    const progressData = {
      1: 75,
      2: 50,
      3: 100,
      4: 25,
    };
    return progressData[courseId] || 0;
  };
  const progress = getProgress(course.id);
  const status = progress === 100 ? "Completed" : "In Progress";
  const progressColor = progress === 100 ? "bg-green-600" : "bg-indigo-600";
  const borderColor =
    progress === 100 ? "border-green-500" : "border-indigo-500";

  return (
    <div
      className={`bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-xl shadow-lg border-l-4 ${borderColor}`}
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h4 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200">
          {course.courseName}
        </h4>
        <span
          className={`${progressColor} text-white px-3 py-1 rounded-full text-sm font-semibold mt-2 sm:mt-0`}
        >
          {status}
        </span>
      </div>
      <p className="text-gray-700 dark:text-gray-400 mb-4 text-sm sm:text-base">
        {course.description}
      </p>
      {/* {progress < 100 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4">
                    <div className="w-full sm:w-64 mr-0 sm:mr-4 mb-2 sm:mb-0">
                        <ProgressBar progress={progress} />
                    </div>
                    <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm sm:text-base">{progress}% Complete</span>
                </div>
            )} */}
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-gray-600 dark:text-gray-400 w-full">
        <span className="font-medium">{t("curriculum")}:</span>
        <span>
          {getLabelByCode(masterData, "curricula", course.curriculum)}
        </span>

        <span className="font-medium">{t("class")}:</span>
        <span>
          {getLabelByCode(masterData, "academicClasses", course.academicClass)}
        </span>

        <span className="font-medium">{t("subject")}:</span>
        <span>{getLabelByCode(masterData, "subjects", course.subject)}</span>
      </div>
      {/* <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-300 text-sm sm:text-base">
                    <span className="mr-2">💻</span> Go to Course
                </button>
                <button className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 text-sm sm:text-base">
                    <span className="mr-2">📄</span> Course Materials
                </button>
            </div> */}
    </div>
  );
};

// Dashboard Section Component
export const Dashboard = ({
  setActiveSection,
  student,
  courses,
  assignments,
  batches,
}) => {
  const studentId = student?.id;
  const filteredAssignments =
    assignments?.filter((a) => a.studentId === studentId) || [];
  const upcomingAssignments =
    filteredAssignments?.filter(
      (a) => new Date(a.expiryDateTime) > new Date()
    ) || [];
  const filteredCourses =
    courses?.filter((c) => c.studentId === studentId) || [];
  const activeCourses =
    filteredCourses?.filter(
      (c) => c.isActive && new Date(c.endDate) > new Date()
    ) || [];
  const filteredBatches =
    batches?.filter((b) => b.studentId === studentId) || [];
  const nextClass = filteredBatches?.find(
    (b) =>
      b.status === "In Progress" && b.course.courseName === "Advanced Algebra"
  );

  return (
    <>
      <section id="dashboard-section" className="mb-12">
        <Card borderColor="border-indigo-500">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            Your Learning Snapshot
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SnapshotCard
              number={`${courses.length}`}
              text="Total Courses"
              colorClass="bg-indigo-50 dark:bg-indigo-950"
            />
            <SnapshotCard
              number={upcomingAssignments.length}
              text="Upcoming Assignments"
              colorClass="bg-purple-50 dark:bg-purple-950"
            />
            <SnapshotCard
              number="2"
              text="New Results"
              colorClass="bg-green-50 dark:bg-green-950"
            />
          </div>
        </Card>
      </section>

      <section className="mb-12">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">
          What's Next?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card borderColor="border-blue-500">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
              <h4 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-200">
                Your Batches
              </h4>
              <span className="text-blue-600 dark:text-blue-400 font-bold text-sm sm:text-base"></span>
            </div>
            {/* <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 mb-2"><span className="mr-2">📚</span> {nextClass?.course?.courseName} - {nextClass?.batchName}</p>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base"><span className="mr-2">👨‍🏫</span> Tutor: {nextClass?.tutor?.name}</p> */}
            <button
              className="mt-6 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 w-full text-sm sm:text-base"
              onClick={() => setActiveSection("my-batches")}
            >
              View All batches
            </button>
          </Card>
          <Card borderColor="border-red-500">
            <h4 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Assignments Due Soon
            </h4>
            <ul className="space-y-3">
              {upcomingAssignments.slice(0, 2).map((a) => (
                <li
                  key={a.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-gray-700 dark:text-gray-300"
                >
                  <div>
                    <p className="font-medium text-base sm:text-base">
                      {a.title}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {a.courseName}
                    </p>
                  </div>
                  <span
                    className={`font-bold text-sm sm:text-base mt-2 sm:mt-0 ${a.statusColor}`}
                  >
                    {a.status}
                  </span>
                </li>
              ))}
            </ul>
            <button
              className="mt-6 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 w-full text-sm sm:text-base"
              onClick={() => setActiveSection("my-assignments")}
            >
              View All Assignments
            </button>
          </Card>
        </div>
      </section>

      <section className="mb-12">
        {/* <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">My Courses</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeCourses.slice(0, 3).map((course) => (
                        <CourseCard key={course.id} course={course} />
                    ))}
                </div> */}
        <div className="text-center mt-8">
          <button
            onClick={() => setActiveSection("my-courses")}
            className="inline-block px-5 py-2.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200 font-semibold rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-800 transition duration-300 text-sm sm:text-base"
          >
            Explore All My Courses <span className="ml-2">➡️</span>
          </button>
        </div>
      </section>
    </>
  );
};

// My Courses Section Component
export const MyCourses = ({ student, courses, masterData }) => {
  console.log(courses);
  return (
    <section id="my-courses-section" className="p-4 pt-0">
      <h3 className="text-2xl sm:text-1xl font-bold text-gray-800 dark:text-gray-200 mb-8">
        My Course Catalog
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} masterData={masterData} />
        ))}
      </div>
    </section>
  );
};

// My Batches Section Component
export const MyBatches = ({ student, batches }) => {
  return (
    <section id="my-batches-section" className="p-4 pt-0">
      <h3 className="text-2xl sm:text-xl font-bold text-gray-800 dark:text-gray-200 mb-8">
        My Batches
      </h3>
      <div className="space-y-6">
        {batches.map((batch) => {
          const courseName = batch.course?.courseName || "Course Not Found";
          return (
            <div
              key={batch.id}
              className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-l-4 border-teal-500"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                <h4 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200">
                  {" "}
                  {batch.batchName}
                </h4>
                <span
                  className={` text-white px-3 py-1 rounded-full text-sm font-semibold mt-2 sm:mt-0`}
                >
                  {batch.status}
                </span>
              </div>

              <h5 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                Course - {courseName}
              </h5>
              {/* <p className="text-gray-700 dark:text-gray-300 mb-2">
                                Schedule: Mondays & Wednesdays, 3:00 PM - 4:30 PM IST
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                Class Tutor: {batch.tutor?.name || 'N/A'}
                            </p> */}
              {/* <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                                View Batch Details
                            </button> */}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// My Tutors Section Component
export const MyTutors = ({ tutors }) => (
  <section id="my-tutors-section" className="p-4 pt-0">
    <h3 className="text-2xl sm:text-xl font-bold text-gray-800 dark:text-gray-200 mb-8">
      My Tutors
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {tutors?.map((tutor) => (
        <div
          key={tutor.id}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg  shadow-lg border-t-4 border-red-500 text-center relative"
        >
          {/* <div className={`absolute top-4 right-4 h-3 w-3 rounded-full ring-2 ring-white ${tutor.online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <Avatar name={tutor.user.firstName} imageUrl={tutor.profilePictureUrl} size="24" /> */}
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">
            {tutor.user.firstName + " " + tutor.user.lastName}
          </h4>
          <p></p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {tutor.bio}
          </p>
          {/* <div className="flex justify-center space-x-4">
                        <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                            <span className="mr-2">🎥</span> Message
                        </button>
                        <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
                            <span className="mr-2">📚</span> Courses
                        </button>
                    </div> */}
        </div>
      ))}
    </div>
  </section>
);

// My Assignments Section Component
export const MyAssignments = ({
  assignments,
  student,
  evaluation,
  course,
  displayToast = () => { },
}) => {
  const studentId = student?.id;
  const [filter, setFilter] = useState("all");
  const { startLoading, stopLoading } = useLoading();

  const navigate = useNavigate();

  const isMobileOrTablet = () => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent || navigator.vendor || "";
    const touchCapable =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    return (
      /android/i.test(ua) ||
      /ipad|iphone|ipod/i.test(ua) ||
      (/macintosh/i.test(ua) && touchCapable) ||
      window.innerWidth <= 1024
    );
  };

  const handleStartAssignment = async (assignment) => {
    try {
      const evalId = assignment?.evaluation?.id;
      const courseObj = assignment?.evaluation?.course || course || {};
      const evalObj = assignment?.evaluation || evaluation || {};
      if (!evalId) {
        console.error("Missing evaluation id for assignment", assignment);
        return;
      }

      if (typeof displayToast === "function" && isMobileOrTablet()) {
        displayToast(
          "For the best experience, please complete assignments on a laptop or desktop.",
          "info"
        );
      }

      // Mark assignment as Started
      try {
        await apiCall(
          `/assignments/${assignment.id}`,
          false,
          "PATCH",
          { id: assignment.id, status: "Started" },
          startLoading,
          stopLoading,
          () => { }
        );
      } catch (e) {
        console.warn("Failed to mark assignment as Started:", e);
      }

      // Fetch questions by codes using master data API method (API-free assessment window)
      let questions = [];
      // Derive question codes from assignment/evaluation; supports string of comma-separated UUIDs
      let questionCodes =
        assignment?.questionCodes || assignment?.evaluation?.questionIds || [];
      if (typeof questionCodes === "string") {
        questionCodes = questionCodes
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      try {
        if (Array.isArray(questionCodes) && questionCodes.length > 0) {
          const res = await masterApiCall(
            "/questions/by-codes",
            false,
            "POST",
            { questionCodes },
            startLoading,
            stopLoading,
            () => { }
          );
          questions = Array.isArray(res) ? res : res?.content ?? [];
        } else {
          console.warn("No questionCodes found on assignment/evaluation.");
          questions = [];
        }
      } catch (e) {
        console.warn("Failed to fetch questions by codes:", e);
        questions = [];
      }

      // Open assessment in a new window
      const w = window.open(
        "/assessment",
        "assessment",
        "width=1200,height=800"
      );
      if (!w) return;

      // Notify main window to pause session timeout
      try {
        window.postMessage({ type: "assessment:started" }, "*");
      } catch (e) {
        console.warn("Failed to pause session timeout:", e);
      }

      // Listen for ready and completion
      const onMessage = async (event) => {
        const data = event?.data;
        if (!data || typeof data !== "object") return;
        if (data.type === "assessment:ready") {
          // Send init payload
          try {
            w.postMessage(
              {
                type: "assessment:init",
                payload: {
                  assignmentId: assignment.id,
                  evaluation: {
                    id: evalObj.id,
                    name: evalObj.name,
                    chapter: evalObj.chapter,
                    maxTimeMinutes: evalObj.maxTimeMinutes,
                  },
                  course: {
                    id: courseObj.id,
                    courseName: courseObj.courseName,
                    subject: courseObj.subject,
                  },
                  student: {
                    id: student?.id,
                    name: student?.name,
                  },
                  timerDuration: evalObj.maxTimeMinutes || 30,
                  questions,
                },
              },
              "*"
            );
          } catch (e) {
            console.warn("Failed to post init message:", e);
          }
        } else if (data.type === "assessment:complete") {
          try {
            const {
              assignmentId,
              score,
              submittedDateTime,
              timeTaken,
              questionMetrics,
            } = data.payload || {};
            if (!assignmentId) return;
            // Submit for review via backend API
            const normalizedMetrics = Array.isArray(questionMetrics)
              ? questionMetrics.map((qm) => {
                const selected = qm.selectedAnswer;
                const skipped = selected === undefined || selected === null;
                let chapterValue = qm.chapter;
                if (chapterValue && typeof chapterValue === "object") {
                  chapterValue =
                    chapterValue.name ||
                    chapterValue.title ||
                    (chapterValue.id != null
                      ? `Chapter ${chapterValue.id}`
                      : "");
                }
                const rawCode =
                  qm.questionCode !== undefined && qm.questionCode !== null
                    ? qm.questionCode
                    : qm.questionId;
                return {
                  questionCode: String(rawCode),
                  questionType: qm.questionType || "MULTIPLE_CHOICE",
                  isCorrect: qm.isCorrect ?? false,
                  timeSpentSeconds: Number(qm.timeSpentSeconds || 0),
                  attempts: Number(qm.attempts || 0),
                  selectedAnswer:
                    selected !== undefined && selected !== null
                      ? String(selected)
                      : null,
                  correctAnswer:
                    qm.correctAnswer !== undefined &&
                      qm.correctAnswer !== null
                      ? String(qm.correctAnswer)
                      : null,
                  skipped: qm.skipped || skipped || false,
                  difficultyLevel: qm.difficultyLevel || "LOW",
                  chapter: chapterValue || "",
                  chapterCode: qm.chapterCode || "",
                  conceptTopic: qm.conceptTopic || "",
                  hintUsed: qm.hintUsed || false,
                  answerChoiceHintUsed: qm.answerChoiceHintUsed || null,
                  questionDetailHintUsed: qm.questionDetailHintUsed || null,
                };
              })
              : [];

            const apiPayload = {
                assignmentId: assignmentId,
                score: Number(score ?? 0),
                timeTaken: Number(timeTaken ?? 0),
                submittedDateTime:
                  (typeof submittedDateTime === "string" &&
                    submittedDateTime) ||
                  new Date().toISOString(),
                chapterCode:
                  (evalObj && evalObj.chapterCode) ||
                  (assignment &&
                    assignment.evaluation &&
                    assignment.evaluation.chapterCode) ||
                  "",
                questionMetrics: normalizedMetrics,
            };

            const response = await apiCall(
              `/assignments/${assignmentId}/submit-for-review`,
              false,
              "POST",
              apiPayload,
              startLoading,
              stopLoading,
              () => { }
            );
            
            // Success: notify assessment window to show results
            if (w && !w.closed) {
              w.postMessage(
                { 
                  type: "assessment:submit-success",
                  payload: {
                    score: data.payload.score,
                    answers: data.payload.answers,
                    questions: data.payload.questions,
                  }
                },
                "*"
              );
            }
            
            await refetch();
            // Resume session timeout after assessment completion
            window.postMessage({ type: "assessment:complete" }, "*");
          } catch (e) {
            console.error("Failed to update assignment after completion:", e);
            if (typeof displayToast === "function") {
              displayToast("Failed to submit assignment", "error");
            }
            
            // Send error details back to assessment window
            if (w && !w.closed) {
              const errorDetails = {
                message: e.message || "Unknown error occurred",
                status: e.response?.status || null,
                statusText: e.response?.statusText || null,
                responseData: e.response?.data || null,
                timestamp: new Date().toISOString(),
              };
              
              // Reconstruct the payload that was sent
              const normalizedMetrics = Array.isArray(data.payload.questionMetrics)
                ? data.payload.questionMetrics.map((qm) => {
                  const selected = qm.selectedAnswer;
                  const skipped = selected === undefined || selected === null;
                  let chapterValue = qm.chapter;
                  if (chapterValue && typeof chapterValue === "object") {
                    chapterValue =
                      chapterValue.name ||
                      chapterValue.title ||
                      (chapterValue.id != null
                        ? `Chapter ${chapterValue.id}`
                        : "");
                  }
                  const rawCode =
                    qm.questionCode !== undefined && qm.questionCode !== null
                      ? qm.questionCode
                      : qm.questionId;
                  return {
                    questionCode: String(rawCode),
                    questionType: qm.questionType || "MULTIPLE_CHOICE",
                    isCorrect: qm.isCorrect ?? false,
                    timeSpentSeconds: Number(qm.timeSpentSeconds || 0),
                    attempts: Number(qm.attempts || 0),
                    selectedAnswer:
                      selected !== undefined && selected !== null
                        ? String(selected)
                        : null,
                    correctAnswer:
                      qm.correctAnswer !== undefined &&
                        qm.correctAnswer !== null
                        ? String(qm.correctAnswer)
                        : null,
                    skipped: qm.skipped || skipped || false,
                    difficultyLevel: qm.difficultyLevel || "LOW",
                    chapter: chapterValue || "",
                    chapterCode: qm.chapterCode || "",
                    conceptTopic: qm.conceptTopic || "",
                    hintUsed: qm.hintUsed || false,
                    answerChoiceHintUsed: qm.answerChoiceHintUsed || null,
                    questionDetailHintUsed: qm.questionDetailHintUsed || null,
                  };
                })
                : [];
              
              const failedPayload = {
                assignmentId: data.payload.assignmentId,
                score: Number(data.payload.score ?? 0),
                timeTaken: Number(data.payload.timeTaken ?? 0),
                submittedDateTime:
                  (typeof data.payload.submittedDateTime === "string" &&
                    data.payload.submittedDateTime) ||
                  new Date().toISOString(),
                chapterCode:
                  (evalObj && evalObj.chapterCode) ||
                  (assignment &&
                    assignment.evaluation &&
                    assignment.evaluation.chapterCode) ||
                  "",
                questionMetrics: normalizedMetrics,
              };
              
              w.postMessage(
                {
                  type: "assessment:submit-error",
                  payload: {
                    error: errorDetails,
                    requestPayload: failedPayload,
                    originalData: data.payload,
                  },
                },
                "*"
              );
            }
          } finally {
            // Keep the assessment window open to show results; keep listener for retake flow
          }
        } else if (data.type === "assessment:retry-submit") {
          // Handle retry submission request from assessment window
          try {
            const { requestPayload } = data.payload || {};
            if (!requestPayload || !requestPayload.assignmentId) {
              throw new Error("Invalid retry payload");
            }

            const response = await apiCall(
              `/assignments/${requestPayload.assignmentId}/submit-for-review`,
              false,
              "POST",
              requestPayload,
              startLoading,
              stopLoading,
              () => { }
            );
            
            // Success: notify assessment window
            if (w && !w.closed) {
              w.postMessage(
                { 
                  type: "assessment:submit-success",
                  payload: data.payload.originalData || {}
                },
                "*"
              );
            }
            
            await refetch();
            window.postMessage({ type: "assessment:complete" }, "*");
            
            if (typeof displayToast === "function") {
              displayToast("Assignment submitted successfully!", "success");
            }
          } catch (e) {
            console.error("Retry submission failed:", e);
            if (typeof displayToast === "function") {
              displayToast("Retry failed. Please try again.", "error");
            }
            
            // Send error back again
            if (w && !w.closed) {
              const errorDetails = {
                message: e.message || "Retry failed",
                status: e.response?.status || null,
                statusText: e.response?.statusText || null,
                responseData: e.response?.data || null,
                timestamp: new Date().toISOString(),
              };
              
              w.postMessage(
                {
                  type: "assessment:submit-error",
                  payload: {
                    error: errorDetails,
                    requestPayload: data.payload.requestPayload,
                    originalData: data.payload.originalData,
                  },
                },
                "*"
              );
            }
          }
        }
      };
      window.addEventListener("message", onMessage);

      // Handle assessment window close - resume session timeout
      const checkWindowClosed = setInterval(() => {
        if (w.closed) {
          clearInterval(checkWindowClosed);
          window.removeEventListener("message", onMessage);
          window.postMessage({ type: "assessment:closed" }, "*");
          console.log("Assessment window closed - session timeout resumed");
        }
      }, 1000);
    } catch (e) {
      console.error("Failed to start assignment:", e);
      if (typeof displayToast === "function") {
        displayToast("Failed to start assignment", "error");
      }
    }
  };

  const {
    assignments: apiAssignments,
    loading: assignmentsLoading,
    refetch,
  } = useAssignments({ studentId });

  const sourceAssignments = apiAssignments ?? assignments ?? [];

  // Filter assignments to match current student
  // API returns student as nested object: assignment.student.id
  let filteredAssignments = sourceAssignments.filter((assignment) => {
    // Handle both flat structure (studentId) and nested structure (student.id)
    const assignmentStudentId = assignment.studentId || assignment.student?.id;

    // If no student ID found, don't show assignment
    if (!assignmentStudentId) {
      return false;
    }

    // Check if assignment belongs to current student
    return String(assignmentStudentId) === String(studentId);
  });

  filteredAssignments = filteredAssignments.sort(
    (a, b) => new Date(b.expiryDateTime) - new Date(a.expiryDateTime)
  );

  const finalFilteredAssignments =
    filteredAssignments.filter((assignment) => {
      if (filter === "all") return true;
      if (filter === "due-soon") {
        const expiryDate = new Date(assignment.expiryDateTime);
        const today = new Date();
        const diffInDays = Math.ceil(
          (expiryDate - today) / (1000 * 60 * 60 * 24)
        );
        return diffInDays <= 3 && diffInDays > 0;
      }
      if (filter === "submitted") return assignment.submittedDateTime != null;
      return false;
    }) || [];

  // const submitAssignment = async (assignment) => {
  //   try {
  //     const payload = {
  //       ...assignment,
  //       status: "Completed",
  //       score: Math.floor(Math.random() * 41) + 60, // 60-100 random
  //       submittedDateTime: new Date().toISOString(),
  //     };
  //     await apiCall(
  //       `/assignments/${assignment.id}`,
  //       false,
  //       "PATCH",
  //       payload,
  //       startLoading,
  //       stopLoading,
  //       () => { }
  //     );
  //     // Refresh list after update
  //     await refetch();
  //   } catch (e) {
  //     console.error("Failed to submit assignment:", e);
  //   }
  // };

  const fmtDate = (iso) => {
    try {
      return iso ? new Date(iso).toLocaleDateString() : "-";
    } catch {
      return iso || "-";
    }
  };

  return (
    <section id="my-assignments-section" className="p-4 pt-0">
      <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
        My Assignments
      </h3>
      <div className="flex flex-wrap space-x-2 mb-6">
        <button
          className={`px-4 py-2 rounded-full font-medium ${filter === "all"
            ? "bg-indigo-600 text-white"
            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          className={`px-4 py-2 rounded-full font-medium ${filter === "due-soon"
            ? "bg-indigo-600 text-white"
            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          onClick={() => setFilter("due-soon")}
        >
          Due Soon
        </button>
        <button
          className={`px-4 py-2 rounded-full font-medium ${filter === "submitted"
            ? "bg-indigo-600 text-white"
            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          onClick={() => setFilter("submitted")}
        >
          Submitted
        </button>
      </div>
      <div className="space-y-4">
        {finalFilteredAssignments.map((assignment) => {
          const title =
            assignment.title || assignment.evaluation?.name || "Evaluation";
          const courseName =
            assignment.courseName ||
            assignment.evaluation?.course?.courseName ||
            "-";
          const now = new Date();
          const expiry = assignment.expiryDateTime
            ? new Date(assignment.expiryDateTime)
            : null;
          let statusLabel = "";
          let statusColor = "";
          if (assignment.submittedDateTime) {
            statusLabel = "Submitted";
            statusColor = "text-green-600";
          } else if (expiry && expiry < now) {
            statusLabel = "Overdue";
            statusColor = "text-red-600";
          } else if (expiry) {
            const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
            console.log(assignment.expiryDateTime);
            console.log(diffDays);
            if (diffDays <= 3) {
              statusLabel = "Assigned - Due Soon";
              statusColor = "text-orange-500";
            } else {
              statusLabel = "Assigned - Upcoming";
              statusColor = "text-gray-500";
            }
          }

          return (
            <div
              key={assignment.id}
              className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-md border-l-4 border-indigo-500 transition duration-200 hover:shadow-lg"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                {/* 1. Title and Metadata (Left Side) */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">
                    {title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {courseName}
                  </p>
                  {statusLabel && (
                    <p className={`text-sm font-bold ${statusColor} dark:text-opacity-80`}>
                      {statusLabel} -{" "}
                      {fmtDate(
                        assignment.submittedDateTime ||
                        assignment.expiryDateTime
                      )}
                    </p>
                  )}
                </div>

                {/* 2. Status Tag (Center/Right, Always Visible) */}
                <div
                  className={`order-first md:order-none inline-block px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap 
                    ${assignment.status === "Completed"
                      ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200"
                      : assignment.status === "InReview"
                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200"
                        : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    }`}
                >
                  {assignment?.status}
                </div>

                {/* 3. Action Button (Right Side, Conditional) */}
                {(assignment.status === "Assigned" || assignment.status === "Started") && (
                  <button
                    // *** FIX APPLIED HERE: Using w-40 for a fixed, consistent width ***
                    className="w-60 px-4 py-2 bg-indigo-600 text-white dark:bg-indigo-700 rounded-lg font-semibold hover:bg-indigo-700 dark:hover:bg-indigo-600 transition duration-150 shadow-md"
                    onClick={() => handleStartAssignment(assignment)}
                  >
                    {assignment.status === "Assigned" && <span>Start Assignment</span>}
                    {assignment.status === "Started" && <span>Resume Assignment</span>}
                  </button>
                )}

              </div>
            </div>
          );
        })}
      </div>
    </section >
  );
};

// My Results Section Component
export const MyResults = ({ student, displayToast }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [viewFeedbackData, setViewFeedbackData] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const { startLoading, stopLoading } = useLoading();
  const [questions, setQuestions] = useState();

  useEffect(() => {
    const init = async () => {
      if (!selectedAssignment || !selectedAssignment.evaluation) return;
      const evaluation = selectedAssignment.evaluation;
      let questionCodesList = evaluation?.questionIds || evaluation?.questionCodes || [];

      if (typeof questionCodesList === "string") {
        questionCodesList = questionCodesList
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }

      if (Array.isArray(questionCodesList) && questionCodesList.length > 0) {
        try {
          const res = await masterApiCall(
            "/questions/by-codes",
            null,
            "POST",
            { questionCodes: questionCodesList },
            startLoading,
            stopLoading,
            displayToast
          );
          const fetchedQuestionsList = Array.isArray(res) ? res : res?.content ?? [];
          setQuestions(fetchedQuestionsList);
        } catch (err) {
          console.error("Error fetching questions:", err);
          stopLoading();
        }
      }
    };
    init();
  }, [selectedAssignment, startLoading, stopLoading, displayToast]);

  const handleOpenFeedback = (assignment) => {
    setViewFeedbackData(assignment);
    setIsFeedbackModalOpen(true);
  };

  const studentId = student?.id;
  const { assignments: apiAssignments, loading: resultsLoading } =
    useAssignments({ studentId });
  let results = (apiAssignments ?? []).filter(
    (a) => a.status === "InReview" || a.status === "Completed"
  );
  results = results.sort(
    (a, b) => new Date(b.submittedDateTime) - new Date(a.submittedDateTime)
  );

  const fmtDate = (iso) => {
    try {
      return iso ? new Date(iso).toLocaleDateString() : "-";
    } catch {
      return iso || "-";
    }
  };

  return (
    <section id="my-results-section" className="p-4 pt-0">
      <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
        My Results
      </h3>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">

        {/* =========================================================================
      1. LOADING STATE (Shared)
      ========================================================================= */}
        {resultsLoading && (
          <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400">
            <div className="flex justify-center items-center flex-col gap-3">
              <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading assignments...</span>
            </div>
          </div>
        )}

        {/* =========================================================================
      2. EMPTY STATE (Shared)
      ========================================================================= */}
        {!resultsLoading && results.length === 0 && (
          <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400 flex flex-col items-center">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
            No submitted assignments found.
          </div>
        )}

        {!resultsLoading && results.length > 0 && (
          <>
            {/* =========================================================================
          3. MOBILE/TABLET VIEW (Cards) - Visible on small screens
          ========================================================================= */}
            <div className="md:hidden grid grid-cols-1 gap-4 p-4 bg-gray-50 dark:bg-gray-900/50">
              {results.map((a) => {
                // Data Prep
                const title = a.title || a.evaluation?.name || "Evaluation";
                const courseName = a.courseName || a.evaluation?.course?.courseName || "-";
                const totalQuestions = a.evaluation?.questionIds?.split(",")?.length;
                const score = a.score != null
                  ? totalQuestions ? `${Math.round((Number(a.score) / Number(totalQuestions)) * 100)}%` : `${a.score}%`
                  : "-";
                const hasFeedback = a.feedback || a.strengthAreas || a.improvementAreas;

                return (
                  <div key={a.id} className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col gap-4">

                    {/* Card Header: Title & Status */}
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight mb-1">{title}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{courseName}</p>
                      </div>
                      <div>
                        {a.status === "InReview" ? (
                          <span className="px-2.5 py-1 inline-flex text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">In Review</span>
                        ) : a.status === "Completed" ? (
                          <span className="px-2.5 py-1 inline-flex text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800">{score}</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </div>

                    {/* Card Meta: Date */}
                    <div className="flex items-center text-xs text-gray-400 dark:text-gray-500">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                      Submitted: {fmtDate(a.submittedDateTime)}
                    </div>

                    {/* Card Actions: Footer */}
                    <div className="pt-4 mt-auto border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">

                      {/* Feedback Action */}
                      <div>
                        {a.status === "Completed" && hasFeedback ? (
                          <button
                            onClick={() => handleOpenFeedback(a)}
                            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
                            Feedback
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300 italic">No feedback</span>
                        )}
                      </div>

                      {/* Open Assignment Action */}
                      <button
                        onClick={() => {
                          setSelectedAssignment(a);
                          setShowModal(true);
                        }}
                        className="px-4 py-2 bg-gray-900 text-white dark:bg-indigo-600 rounded-md text-sm font-medium shadow hover:bg-gray-800 dark:hover:bg-indigo-500 transition-colors"
                      >
                        Open
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* =========================================================================
          4. DESKTOP VIEW (Table) - Hidden on mobile, visible on medium+ screens
          ========================================================================= */}
            <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignment</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Course</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Score</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted On</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {results.map((a) => {
                    // Data Prep (Repeated for desktop scope)
                const title = a.title || a.evaluation?.name || "Evaluation";
                    const courseName = a.courseName || a.evaluation?.course?.courseName || "-";
                    const totalQuestions = a.evaluation?.questionIds?.split(",")?.length;
                    const score = a.score != null
                      ? totalQuestions ? `${Math.round((Number(a.score) / Number(totalQuestions)) * 100)}%` : `${a.score}%`
                    : "-";
                    const hasFeedback = a.feedback || a.strengthAreas || a.improvementAreas;

                return (
                      <tr key={a.id} className="group hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{courseName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                          {a.status === "InReview" ? (
                            <span className="px-2.5 py-0.5 inline-flex text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800">In Review</span>
                          ) : a.status === "Completed" ? (
                            <span className="px-2.5 py-0.5 inline-flex text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">{score}</span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                    </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{fmtDate(a.submittedDateTime)}</td>

                        {/* Desktop Actions Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-6">
                            {/* Feedback Button */}
                            {a.status === "Completed" && hasFeedback ? (
                              <button onClick={() => handleOpenFeedback(a)} className="flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
                                <span className="text-xs font-medium underline decoration-slate-300 underline-offset-4 group-hover:decoration-indigo-300">Show Feedback</span>
                              </button>
                            ) : (
                              <span className="text-gray-300 text-xs italic">No feedback</span>
                            )}

                            {/* Open Button */}
                      <button
                              onClick={() => { setSelectedAssignment(a); setShowModal(true); }}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium text-sm transition-colors"
                            >
                              Open
                      </button>
                          </div>
                    </td>
                  </tr>
                );
                  })}
          </tbody>
        </table>
            </div>
          </>
        )}

        {/* =========================================================================
      5. MODALS (External to the grid/table structure)
      ========================================================================= */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden relative border border-gray-200 dark:border-gray-800">
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10 bg-white dark:bg-gray-900 rounded-full p-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
              <QuestionMetricsPanel assignment={selectedAssignment} questions={questions} displayToast={displayToast} onListView={() => setShowModal(false)} />
            </div>
          </div>
        )}

        <FeedbackViewModal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)} data={viewFeedbackData} />

      </div>
    </section>
  );
};


// ==========================================
// Component: Read-Only Feedback Modal
// ==========================================
const FeedbackViewModal = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm transition-opacity">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-3xl w-full flex flex-col max-h-[85vh] border border-slate-200 dark:border-slate-700 overflow-hidden">

        {/* Header */}
        <div className="bg-slate-100 dark:bg-slate-900/80 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white dark:bg-slate-800 rounded-full shadow-sm text-indigo-600 dark:text-indigo-400">
              {/* Chat Bubble Icon */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Tutor Feedback</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                for {data.title || "Assignment"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            <span className="sr-only">Close</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 bg-white dark:bg-slate-800 space-y-6">

          {/* General Feedback */}
          {data.feedback && (
            <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
              <h4 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                General Feedback
              </h4>
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                {data.feedback}
              </p>
            </div>
          )}

          {/* Split Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Strengths */}
            {data.strengthAreas && (
              <div className="bg-emerald-50/50 dark:bg-emerald-900/10 p-5 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                <h4 className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
                  Strengths
                </h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {data.strengthAreas}
                </p>
              </div>
            )}

            {/* Improvements */}
            {data.improvementAreas && (
              <div className="bg-amber-50/50 dark:bg-amber-900/10 p-5 rounded-xl border border-amber-100 dark:border-amber-800/30">
                <h4 className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path></svg>
                  Improvements
                </h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {data.improvementAreas}
                </p>
              </div>
            )}
          </div>

          {!data.feedback && !data.strengthAreas && !data.improvementAreas && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No detailed feedback provided.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-end">
              <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm transition-all focus:ring-2 focus:ring-slate-400 dark:bg-indigo-600 dark:hover:bg-indigo-500"
              >
            Close
              </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Component: My Concepts
// ==========================================
export const MyConcepts = ({ student, courses, batches, masterData, displayToast, onStartLearning }) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedConcepts, setExpandedConcepts] = useState({});
  const [subjectCodes, setSubjectCodes] = useState([]);
  const [classCodes, setClassCodes] = useState([]);
  const [adaptiveProgress, setAdaptiveProgress] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState("All");

  const uniqueChapters = React.useMemo(() => {
    const optionsMap = new Map();
    optionsMap.set("All", { value: "All", label: "All Chapters" });

    if (!masterData?.rawChapters || concepts.length === 0) {
      return Array.from(optionsMap.values());
    }

    const conceptIds = new Set(concepts.map(c => c.id));

    masterData.rawChapters.forEach(chapter => {
      const chapterConceptIds = chapter.conceptChapters?.map(cc => cc.concept?.id) || [];
      const hasOverlap = chapterConceptIds.some(id => conceptIds.has(id));
      
      if (hasOverlap) {
        optionsMap.set(String(chapter.id), { value: String(chapter.id), label: chapter.name });
      }
    });

    return Array.from(optionsMap.values());
  }, [concepts, masterData]);

  const filteredConcepts = React.useMemo(() => {
    if (selectedChapter === "All") return concepts;
    
    const selectedChapterData = masterData?.rawChapters?.find(ch => String(ch.id) === selectedChapter);
    if (!selectedChapterData) return [];

    const chapterConceptIds = new Set(selectedChapterData.conceptChapters?.map(cc => cc.concept?.id) || []);

    return concepts.filter(c => chapterConceptIds.has(c.id));
  }, [concepts, selectedChapter, masterData]);

  const selectedChapterName = React.useMemo(() => {
    if (selectedChapter === "All") return null;
    const chapterObj = uniqueChapters.find(ch => ch.value === selectedChapter);
    return chapterObj ? chapterObj.label : null;
  }, [selectedChapter, uniqueChapters]);

  // Extract unique subject codes and class codes from enrolled courses/batches
  useEffect(() => {
    const extractSubjectsAndClasses = () => {
      const subjects = new Set();
      const classes = new Set();
      
      // From courses
      courses?.forEach((course) => {
        if (course.subject) {
          subjects.add(course.subject);
        }
        if (course.academicClass) {
          classes.add(course.academicClass);
        }
      });
      
      // From batches' courses
      batches?.forEach((batch) => {
        if (batch.course?.subject) {
          subjects.add(batch.course.subject);
        }
        if (batch.course?.academicClass) {
          classes.add(batch.course.academicClass);
        }
      });
      
      const subjectArray = Array.from(subjects).filter(Boolean);
      const classArray = Array.from(classes).filter(Boolean);
      setSubjectCodes(subjectArray);
      setClassCodes(classArray);
    };
    
    extractSubjectsAndClasses();
  }, [courses, batches]);

  // Fetch concepts by subject codes and class codes
  useEffect(() => {
    const fetchConcepts = async () => {
      if (subjectCodes.length === 0) return;
      
      setLoading(true);
      try {
        const subjectsQuery = subjectCodes.join(",");
        const classesQuery = classCodes.length > 0 ? classCodes.join(",") : "";
        
        // Use the endpoint that accepts both subjects and classes
        const endpoint = classesQuery
          ? `/concepts/by-subjects-and-classes?subjects=${subjectsQuery}&classes=${classesQuery}`
          : `/concepts/by-subjects?subjects=${subjectsQuery}`;
        
        const data = await masterApiCall(
          endpoint,
          false,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setConcepts(data || []);
      } catch (err) {
        console.error("Failed to fetch concepts:", err);
        if (typeof displayToast === "function") {
          displayToast("Failed to load concepts", "error");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchConcepts();
  }, [subjectCodes, classCodes, startLoading, stopLoading, displayToast]);

  // Fetch adaptive learning progress
  useEffect(() => {
    const fetchAdaptiveProgress = async () => {
      if (!student?.id) return;
      
      try {
        const storedToken = sessionStorage.getItem("keycloak_access_token");
        const baseURL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8081/backend";
        
        // Use student.id (integer database ID from the profile being viewed)
        const response = await fetch(
          `${baseURL}/adaptive-learning/progress/${student.id}`,
          {
            headers: {
              Authorization: `Bearer ${storedToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAdaptiveProgress(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch adaptive learning progress:", err);
        // Silent fail - progress is optional
      }
    };
    
    fetchAdaptiveProgress();
  }, [student]);

  const toggleConcept = (conceptId) => {
    setExpandedConcepts((prev) => ({
      ...prev,
      [conceptId]: !prev[conceptId],
    }));
  };

  const getTierBadge = (tier) => {
    const tiers = {
      BEGINNER: { icon: "🌱", name: "Beginner", color: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
      BRONZE: { icon: "🥉", name: "Bronze", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
      SILVER: { icon: "🥈", name: "Silver", color: "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200" },
      GOLD: { icon: "🥇", name: "Gold", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" },
      MASTERED: { icon: "👑", name: "Mastered", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
    };
    return tiers[tier] || tiers.BEGINNER;
  };

  const getConceptProgress = (conceptCode) => {
    return adaptiveProgress.find((p) => p.conceptCode === conceptCode);
  };

  const handleStartPractice = (concept) => {
    // Use student.id (integer database ID from the profile being viewed)
    if (!student?.id) {
      console.error("Student profile not found");
      displayToast?.("Unable to start practice session", "error");
      return;
    }
    
    const progress = getConceptProgress(concept.code);
    const conceptProgress = {
      conceptCode: concept.code,
      conceptName: concept.name,
      subject: concept.subject,
      grade: concept.grade || student?.grade,
      progressTier: progress?.progressTier || "BEGINNER",
      masteryPercentage: progress?.masteryPercentage || 0,
      totalAttempts: progress?.totalAttempts || 0,
      totalQuestionsAnswered: progress?.totalQuestionsAnswered || 0,
      totalCorrectAnswers: progress?.totalCorrectAnswers || 0,
      averageScore: progress?.averageScore || 0,
      lastAttemptDate: progress?.lastAttemptDate,
      recommendedQuestions: progress?.recommendedQuestions || 10,
      studentId: student.id, // Pass integer database ID from the profile being viewed
    };

    if (onStartLearning) {
      onStartLearning(conceptProgress);
    }
  };

  if (loading) {
    return (
      <section id="my-concepts-section" className="p-4 pt-0">
        <h3 className="text-2xl sm:text-xl font-bold text-gray-800 dark:text-gray-200 mb-8">
          My Learning Concepts
        </h3>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-4 text-gray-600 dark:text-gray-400">Loading concepts...</span>
            </div>
      </section>
    );
  }

  if (concepts.length === 0) {
    return (
      <section id="my-concepts-section" className="p-4 pt-0">
        <h3 className="text-2xl sm:text-xl font-bold text-gray-800 dark:text-gray-200 mb-8">
          My Learning Concepts
        </h3>
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-md">
          <div className="text-6xl mb-4">📚</div>
          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">
            No concepts available for your enrolled subjects yet.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Enroll in courses to explore learning concepts!
          </p>
          </div>
      </section>
    );
  }

  return (
    <section id="my-concepts-section" className="p-4 pt-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h3 className="text-2xl sm:text-xl font-bold text-gray-800 dark:text-gray-200 m-0">
            My Learning Concepts
          </h3>
          {selectedChapterName && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Chapter: {selectedChapterName}
            </p>
          )}
        </div>
        
        {uniqueChapters.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="chapter-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Filter by Chapter:
            </label>
            <select
              id="chapter-select"
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2 sm:p-2.5"
            >
              {uniqueChapters.map(chapter => (
                <option key={chapter.value} value={chapter.value}>{chapter.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      <div className="space-y-4">
        {filteredConcepts.map((concept) => {
          const isConceptExpanded = expandedConcepts[concept.id];
          
          return (
            <div
              key={concept.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Concept Header */}
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleConcept(concept.id)}
                    className="flex-1 flex items-center gap-3 hover:opacity-80 transition-opacity text-left"
                  >
                    <span className="text-2xl">🎯</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {concept.name}
                        </h4>
                        {(() => {
                          const progress = getConceptProgress(concept.code);
                          if (progress) {
                            const tierInfo = getTierBadge(progress.progressTier);
                            return (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${tierInfo.color}`}>
                                <span>{tierInfo.icon}</span>
                                <span>{tierInfo.name}</span>
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {concept.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {concept.description}
                        </p>
                      )}
                      {(() => {
                        const progress = getConceptProgress(concept.code);
                        if (progress && progress.masteryPercentage > 0) {
                          return (
                            <div className="mt-2 flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden max-w-xs">
                                <div
                                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
                                  style={{ width: `${progress.masteryPercentage}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                {progress.masteryPercentage.toFixed(0)}%
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${
                        isConceptExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                
                {/* Practice Button */}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartPractice(concept);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium text-sm rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {(() => {
                      const progress = getConceptProgress(concept.code);
                      return progress && progress.totalAttempts > 0 ? "Continue Practice" : "Start Practice";
                    })()}
                  </button>
                  {(() => {
                    const progress = getConceptProgress(concept.code);
                    if (progress && progress.totalAttempts > 0) {
                      return (
                        <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                          <span>📝 {progress.totalAttempts} attempts</span>
                          <span>✅ {progress.totalCorrectAnswers}/{progress.totalQuestionsAnswered}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Concept Units - Simple List (no progress tracking on units) */}
              {isConceptExpanded && concept.conceptUnits && concept.conceptUnits.length > 0 && (
                <div className="px-6 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    📚 Learning Units ({concept.conceptUnits.length})
                  </p>
                  <div className="space-y-2">
                    {concept.conceptUnits
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((unit) => (
                        <div
                          key={unit.id}
                          className="ml-4 pl-4 border-l-2 border-indigo-300 dark:border-indigo-600 py-2"
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-base mt-0.5">📖</span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 dark:text-gray-200">
                                {unit.name || `Unit ${unit.order || ""}`}
                              </p>
                              {unit.description && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {unit.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};